import { registerLocaleData } from '@angular/common';
import {
    HTTP_INTERCEPTORS,
    provideHttpClient,
    withInterceptorsFromDi
} from '@angular/common/http';
import en from '@angular/common/locales/en';
import {
    ApplicationConfig,
    importProvidersFrom,
    inject,
    provideAppInitializer,
    provideBrowserGlobalErrorListeners,
    provideZoneChangeDetection
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { JwtModule } from '@auth0/angular-jwt';
import { Drivers } from '@ionic/storage';
import { IonicStorageModule } from '@ionic/storage-angular';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { CookieService } from '@zlib/services/cookie.services';
import { ElectronService } from '@zlib/services/electron.service';
import { AuthGuard } from '@zmodules/auth/auth.guard';
import { AuthModule } from '@zmodules/auth/auth.module';
import { PermissionGuard } from '@zmodules/auth/permission.guard';
import { RoleGuard } from '@zmodules/auth/role.guard';
import { CubejsClientModule } from '@zmodules/cubejs-client-ngx/module';
import { ResourceUtil } from '@zshared/utilities/resource';
import * as echarts from 'echarts/core';
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { en_US, provideNzI18n } from 'ng-zorro-antd/i18n';
import { NgxEchartsModule } from 'ngx-echarts';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { IconsProviderModule } from './icons-provider.module';
import { LayoutModule } from './layouts/layout.module';
import { TokenInterceptor } from '@zlib/services/token.interceptor';

registerLocaleData(en);

export const appConfig: ApplicationConfig = {
    providers: [
        ResourceUtil,
        CookieService,
        AuthGuard,
        RoleGuard,
        PermissionGuard,
        importProvidersFrom(
            IconsProviderModule,
            LayoutModule,
            AuthModule,
            KeycloakAngularModule,
            JwtModule.forRoot({
                config: {
                    tokenGetter: () =>
                        localStorage ? localStorage.getItem('token') : ''
                }
            }),
            NgxEchartsModule.forRoot({ echarts }),
            CubejsClientModule.forRoot({
                token: () =>
                    Promise.resolve(localStorage.getItem('token') || ''),
                options: { apiUrl: environment.analytics.url, method: 'POST' }
            }),
            IonicStorageModule.forRoot({
                name: '__invoice',
                driverOrder: [Drivers.IndexedDB, Drivers.LocalStorage]
            })
        ),
        provideHttpClient(withInterceptorsFromDi()),
        provideTranslateService({
            lang: 'vi',
            fallbackLang: 'en',
            loader: provideTranslateHttpLoader({
                prefix: '/assets/i18n/',
                suffix: '.json?v=' + environment.version
            })
        }),
        provideAppInitializer((): Promise<boolean> => {
            const idToken = localStorage.getItem('token');
            const refreshToken = localStorage.getItem('refresh_token');
            const electronService = new ElectronService();
            const initOptions: any = { idToken, refreshToken };

            if (!electronService.isElectronApp) {
                initOptions.onLoad = 'check-sso';
                initOptions.silentCheckSsoRedirectUri =
                    window.location.origin + '/assets/silent-check-sso.html';
            }
            const keycloakService = inject(KeycloakService);

            // Return the Promise directly from keycloakService.init()
            return keycloakService.init({
                config: {
                    url: environment.id.url,
                    realm: environment.id.realm,
                    clientId: environment.id.clientId
                },
                initOptions: initOptions,
                bearerExcludedUrls: ['/assets', '/clients/public']
            });
        }),
        { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
        provideAnimations(),
        provideBrowserGlobalErrorListeners(),
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideRouter(routes),
        provideNzI18n(en_US)
    ]
};
